import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import FormatControls from './components/FormatControls';
import { DEFAULT_TEXT_STYLE } from './constants';
import './styles/fonts.css';

const parseColorToRGB = (color) => {
  if (!color) return { r: 0, g: 0, b: 0 };
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b };
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b };
    }
  }
  if (color.startsWith('rgb')) {
    const nums = color
      .replace(/rgba?\(/, '')
      .replace(/\)/, '')
      .split(',')
      .map((n) => parseFloat(n.trim()))
      .slice(0, 3);
    const [r = 0, g = 0, b = 0] = nums;
    return { r, g, b };
  }
  return { r: 0, g: 0, b: 0 };
};

const isLightColor = (color) => {
  const { r, g, b } = parseColorToRGB(color || '#000');
  const brightness = (r * 299 + g * 587 + b * 114) / 1000; // 0..255
  return brightness > 186;
};

const getEditorContrastBackground = (textColor) =>
  isLightColor(textColor) ? '#111827' /* gray-900 */ : '#ffffff';

export default function TextEditor({
  isOpen,
  onClose,
  content = '',
  style = {},
  onSave,
}) {
  const [editorState, setEditorState] = useState({
    content: content,
    style: {
      ...DEFAULT_TEXT_STYLE,
      ...style,
    },
  });

  const handleSave = () => {
    onSave({
      content: editorState.content,
      style: editorState.style,
    });
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ zIndex: 11000 }}
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

        <div className="relative bg-white rounded-xl shadow-lg w-full max-w-2xl mx-4">
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Edit Text
            </Dialog.Title>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="p-4 border-b">
            <FormatControls
              style={editorState.style}
              onStyleChange={(newStyle) => {
                // If font family is being changed to Bangers, reset bold and italic
                if (newStyle.fontFamily === 'Bangers') {
                  setEditorState((prev) => ({
                    ...prev,
                    style: {
                      ...prev.style,
                      ...newStyle,
                      fontWeight: undefined,
                      fontStyle: undefined,
                    },
                  }));
                } else {
                  setEditorState((prev) => ({
                    ...prev,
                    style: { ...prev.style, ...newStyle },
                  }));
                }
              }}
            />
          </div>

          <div className="p-4">
            <textarea
              value={editorState.content}
              onChange={(e) =>
                setEditorState((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
              className="w-full min-h-[200px] p-4 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              style={{
                fontFamily:
                  editorState.style.fontFamily === 'Great Vibes'
                    ? "'Great Vibes', cursive"
                    : editorState.style.fontFamily,
                fontSize: editorState.style.fontSize,
                lineHeight: editorState.style.lineHeight,
                color: editorState.style.color,
                textAlign:
                  editorState.style.textAlign === 'center'
                    ? 'center'
                    : editorState.style.textAlign === 'right'
                    ? 'right'
                    : 'left',
                fontWeight:
                  editorState.style.fontWeight === 'bold' ? 'bold' : 'normal',
                fontStyle:
                  editorState.style.fontStyle === 'italic' ? 'italic' : 'normal',
                textDecoration:
                  editorState.style.textDecoration === 'underline'
                    ? 'underline'
                    : 'none',
                backgroundColor: getEditorContrastBackground(editorState.style.color),
                caretColor: editorState.style.color,
              }}
              placeholder="Enter your text here..."
            />
          </div>

          <div className="flex justify-end gap-3 p-4 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
